import { jsx } from 'react/jsx-runtime';
import { safeRest } from '@wds/utils';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'react-jss';
import { createMemoStyles } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStyles$2 = createMemoStyles(function () {
  var FadeStyles = {
    wrenchFade: {
      '&$entering, &$exiting': {
        transition: function (_a) {
          var transitionTime = _a.transitionTime;
          return "opacity ".concat(transitionTime, "ms linear");
        }
      },
      '&$enterActive': {
        transition: function (_a) {
          var transitionTime = _a.transitionTime;
          return "opacity ".concat(transitionTime, "ms linear");
        }
      },
      '&$exitActive': {
        transition: function (_a) {
          var transitionTime = _a.transitionTime;
          return "opacity ".concat(transitionTime, "ms linear");
        }
      }
    },
    enter: {
      opacity: '0'
    },
    enterActive: {
      opacity: '1'
    },
    exit: {
      opacity: '1'
    },
    exitActive: {
      opacity: '0'
    },
    entering: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent;
      return !alwaysRenderContent ? undefined : {
        opacity: '1',
        visibility: 'visible'
      };
    },
    entered: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent;
      return !alwaysRenderContent ? undefined : {
        opacity: '1',
        visibility: 'visible'
      };
    },
    exiting: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent;
      return !alwaysRenderContent ? undefined : {
        opacity: '0',
        visibility: 'visible'
      };
    },
    exited: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent;
      return !alwaysRenderContent ? undefined : {
        opacity: '0',
        visibility: 'hidden',
        height: '0',
        width: '0'
      };
    },
    unmounted: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent;
      return !alwaysRenderContent ? undefined : {
        opacity: '0'
      };
    }
  };
  return FadeStyles;
});

var Fade = function Fade(_a) {
  var children = _a.children,
    _b = _a.speed,
    speed = _b === void 0 ? 'normal' : _b,
    timeout = _a.timeout,
    _c = _a.alwaysRenderContent,
    alwaysRenderContent = _c === void 0 ? false : _c,
    rest = __rest(_a, ["children", "speed", "timeout", "alwaysRenderContent"]);
  var motion = useTheme().motion;
  var transitionTime = timeout !== null && timeout !== void 0 ? timeout : motion.fade.speed[speed];
  var _d = useStyles$2({
      transitionTime: transitionTime,
      alwaysRenderContent: alwaysRenderContent
    }),
    wrenchFade = _d.wrenchFade,
    animationClasses = __rest(_d, ["wrenchFade"]);
  if (!alwaysRenderContent) {
    return jsx(CSSTransition, __assign({
      className: wrenchFade,
      classNames: __assign({}, animationClasses),
      unmountOnExit: true,
      timeout: transitionTime
    }, safeRest(rest), {
      children: jsx("div", {
        children: children
      })
    }));
  }
  return jsx(CSSTransition, __assign({
    className: wrenchFade,
    classNames: __assign({}, animationClasses),
    timeout: transitionTime
  }, safeRest(rest), {
    children: function (state) {
      return jsx("div", {
        className: "".concat(wrenchFade, " ").concat(animationClasses[state]),
        children: children
      });
    }
  }));
};

var speedAnimation = function speedAnimation(isEnter, speed) {
  var curveEntrance = 'cubic-bezier(0, 0, 0, 1)';
  var curveEase = 'cubic-bezier(0.2, 0, 0.1, 1)';
  var bezier = isEnter ? curveEntrance : curveEase;
  var transform = "transform ".concat(speed, "ms ").concat(bezier);
  var opacity = "opacity ".concat(speed / (isEnter ? 5 : 2), "ms linear ").concat(speed / (isEnter ? 10 : 2), "ms");
  return "".concat(transform, ", ").concat(opacity);
};
var directionAnimation = function directionAnimation(direction, distance, isZero) {
  return {
    up: "translateY(".concat(isZero ? 0 : distance, "%)"),
    down: "translateY(-".concat(isZero ? 0 : distance, "%)"),
    left: "translateX(".concat(isZero ? 0 : distance, "%)"),
    right: "translateX(-".concat(isZero ? 0 : distance, "%)")
  }[direction];
};
var useStyles$1 = createMemoStyles(function () {
  var SlideStyles = {
    wrenchSlide: {
      '&$enter': {
        transform: function (_a) {
          var direction = _a.direction,
            slideDistance = _a.slideDistance;
          return directionAnimation(direction, slideDistance, false);
        }
      },
      '&$enterActive': {
        transition: function (_a) {
          var slideSpeed = _a.slideSpeed;
          return speedAnimation(true, slideSpeed);
        },
        transform: function (_a) {
          var direction = _a.direction,
            slideDistance = _a.slideDistance;
          return directionAnimation(direction, slideDistance, true);
        }
      },
      '&$exit': {
        transform: function (_a) {
          var direction = _a.direction,
            slideDistance = _a.slideDistance;
          return directionAnimation(direction, slideDistance, true);
        }
      },
      '&$exitActive': {
        transition: function (_a) {
          var slideSpeed = _a.slideSpeed;
          return speedAnimation(false, slideSpeed);
        },
        transform: function (_a) {
          var direction = _a.direction,
            slideDistance = _a.slideDistance;
          return directionAnimation(direction, slideDistance, false);
        }
      }
    },
    enter: function (_a) {
      var direction = _a.direction,
        slideDistance = _a.slideDistance;
      return {
        opacity: '0',
        transform: directionAnimation(direction, slideDistance, false)
      };
    },
    enterActive: function (_a) {
      var direction = _a.direction,
        slideDistance = _a.slideDistance;
        _a.slideSpeed;
      return {
        opacity: '1',
        transform: directionAnimation(direction, slideDistance, true)
      };
    },
    exit: function (_a) {
      var direction = _a.direction,
        slideDistance = _a.slideDistance;
      return {
        opacity: '1',
        transform: directionAnimation(direction, slideDistance, true)
      };
    },
    exitActive: function (_a) {
      var direction = _a.direction,
        slideDistance = _a.slideDistance;
        _a.slideSpeed;
      return {
        opacity: '0',
        transform: directionAnimation(direction, slideDistance, false)
      };
    },
    entering: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent,
        slideDistance = _a.slideDistance,
        direction = _a.direction,
        slideSpeed = _a.slideSpeed;
      return !alwaysRenderContent ? undefined : {
        opacity: '1',
        visibility: 'visible',
        transform: directionAnimation(direction, slideDistance, false),
        transition: speedAnimation(true, slideSpeed)
      };
    },
    entered: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent,
        slideDistance = _a.slideDistance,
        direction = _a.direction;
      return !alwaysRenderContent ? undefined : {
        opacity: '1',
        visibility: 'visible',
        transform: directionAnimation(direction, slideDistance, true)
      };
    },
    exiting: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent,
        slideDistance = _a.slideDistance,
        direction = _a.direction,
        slideSpeed = _a.slideSpeed;
      return !alwaysRenderContent ? undefined : {
        opacity: '0',
        visibility: 'visible',
        transform: directionAnimation(direction, slideDistance, true),
        transition: speedAnimation(false, slideSpeed)
      };
    },
    exited: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent,
        slideDistance = _a.slideDistance,
        direction = _a.direction;
      return !alwaysRenderContent ? undefined : {
        opacity: '0',
        visibility: 'hidden',
        height: '0',
        transform: directionAnimation(direction, slideDistance, false)
      };
    },
    unmounted: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent;
      return !alwaysRenderContent ? undefined : {
        opacity: '0'
      };
    }
  };
  return SlideStyles;
});

var Slide = function Slide(_a) {
  var children = _a.children,
    timeout = _a.timeout,
    _b = _a.direction,
    direction = _b === void 0 ? 'up' : _b,
    _c = _a.speed,
    speed = _c === void 0 ? 'normal' : _c,
    _d = _a.distance,
    distance = _d === void 0 ? 'normal' : _d,
    _e = _a.alwaysRenderContent,
    alwaysRenderContent = _e === void 0 ? false : _e,
    rest = __rest(_a, ["children", "timeout", "direction", "speed", "distance", "alwaysRenderContent"]);
  var motion = useTheme().motion;
  var slideSpeed = timeout !== null && timeout !== void 0 ? timeout : motion.slide.speed[speed];
  var slideDistance = motion.slide.distance[distance];
  var _f = useStyles$1({
      slideSpeed: slideSpeed,
      direction: direction,
      slideDistance: slideDistance,
      alwaysRenderContent: alwaysRenderContent
    }),
    wrenchSlide = _f.wrenchSlide,
    animationClasses = __rest(_f, ["wrenchSlide"]);
  if (!alwaysRenderContent) {
    return jsx(CSSTransition, __assign({
      className: wrenchSlide,
      classNames: __assign({}, animationClasses),
      timeout: slideSpeed,
      unmountOnExit: true
    }, safeRest(rest), {
      children: jsx("div", {
        children: children
      })
    }));
  }
  return jsx(CSSTransition, __assign({
    className: wrenchSlide,
    classNames: __assign({}, animationClasses),
    timeout: slideSpeed
  }, safeRest(rest), {
    children: function (state) {
      return jsx("div", {
        className: "".concat(wrenchSlide, " ").concat(animationClasses[state]),
        children: children
      });
    }
  }));
};

var bezier = 'cubic-bezier(0, 0, 0, 1)';
var useStyles = createMemoStyles(function () {
  var ZoomStyles = {
    wrenchZoom: {
      '&$enterActive': {
        transition: function (_a) {
          var timeout = _a.timeout;
          return "transform ".concat(timeout, "ms ").concat(bezier, ", opacity 100ms linear");
        }
      },
      '&$exitActive': {
        transition: function (_a) {
          var timeout = _a.timeout;
          return "transform ".concat(timeout, "ms ").concat(bezier, ", opacity 50ms linear 50ms");
        }
      }
    },
    enter: {
      opacity: '0',
      transform: 'scale(0.8)'
    },
    enterActive: {
      opacity: '1',
      transform: 'scale(1)'
    },
    exit: {
      opacity: '1',
      transform: 'scale(1)'
    },
    exitActive: {
      opacity: '0',
      transform: 'scale(0.8)'
    },
    entering: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent,
        timeout = _a.timeout;
      return !alwaysRenderContent ? undefined : {
        opacity: '1',
        visibility: 'visible',
        transform: 'scale(1)',
        transition: "transform ".concat(timeout, "ms ").concat(bezier, ", opacity 100ms linear")
      };
    },
    entered: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent;
      return !alwaysRenderContent ? undefined : {
        opacity: '1',
        visibility: 'visible',
        transform: 'scale(1)'
      };
    },
    exiting: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent,
        timeout = _a.timeout;
      return !alwaysRenderContent ? undefined : {
        opacity: '0',
        visibility: 'visible',
        transform: 'scale(0.8)',
        transition: "transform ".concat(timeout, "ms ").concat(bezier, ", opacity 50ms linear 50ms")
      };
    },
    exited: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent;
      return !alwaysRenderContent ? undefined : {
        opacity: '0',
        visibility: 'hidden',
        height: '0',
        transform: 'scale(0.8)'
      };
    },
    unmounted: function (_a) {
      var alwaysRenderContent = _a.alwaysRenderContent;
      return !alwaysRenderContent ? undefined : {
        opacity: '0'
      };
    }
  };
  return ZoomStyles;
});

var Zoom = function Zoom(_a) {
  var children = _a.children,
    _b = _a.timeout,
    timeout = _b === void 0 ? 250 : _b,
    _c = _a.alwaysRenderContent,
    alwaysRenderContent = _c === void 0 ? false : _c,
    rest = __rest(_a, ["children", "timeout", "alwaysRenderContent"]);
  var _d = useStyles({
      timeout: timeout,
      alwaysRenderContent: alwaysRenderContent
    }),
    wrenchZoom = _d.wrenchZoom,
    animationClasses = __rest(_d, ["wrenchZoom"]);
  if (!alwaysRenderContent) {
    return jsx(CSSTransition, __assign({
      className: wrenchZoom,
      classNames: __assign({}, animationClasses),
      unmountOnExit: true,
      timeout: timeout
    }, safeRest(rest), {
      children: jsx("div", {
        children: children
      })
    }));
  }
  return jsx(CSSTransition, __assign({
    className: wrenchZoom,
    classNames: __assign({}, animationClasses),
    timeout: timeout
  }, safeRest(rest), {
    children: function (state) {
      return jsx("div", {
        className: "".concat(wrenchZoom, " ").concat(animationClasses[state]),
        children: children
      });
    }
  }));
};

var MOTION_SPEEDS = ['slow', 'normal', 'fast'];
var MOTION_DIRECTIONS = ['up', 'right', 'down', 'left'];
var MOTION_DISTANCES = ['normal', 'near', 'far'];

export { Fade, MOTION_DIRECTIONS, MOTION_DISTANCES, MOTION_SPEEDS, Slide, Zoom };
